import React, { useState, useEffect, ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { useAuth } from "./shared/AuthContext";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { Button } from "./ui";
import { authService } from "../services/auth.service";

interface SidebarComponentProps {
  children: ReactNode;
}

interface NavItem {
  path: string;
  icon: string;
  label: string;
}

const NAV_ITEMS: NavItem[] = [
  { path: "/dashboard", icon: "bi-speedometer2", label: "Dashboard" },
  { path: "/orders", icon: "bi-table", label: "Orders" },
  { path: "/users", icon: "bi-person-badge", label: "Users" },
  { path: "/create-supplier", icon: "bi-truck", label: "Suppliers" },
  { path: "/create-client", icon: "bi-building", label: "Branchs" },
  { path: "/monthly-summary", icon: "bi-calendar3", label: "Summary" },
];

const BrandChip = ({ className = "" }: { className?: string }) => (
  <span
    className={`bg-brand/10 text-brand-strong ring-brand/20 inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold tracking-tight ring-1 ${className}`}
  >
    <span className="font-bold tracking-[0.18em]">ERP</span>
    <span className="font-medium">Admin</span>
  </span>
);

/** Nav list + user footer. Shared by the desktop column and the mobile drawer. */
const SidebarNav = ({ closeButton }: { closeButton?: ReactNode }) => {
  const { user } = useAuth();

  return (
    <>
      <div className="flex items-center justify-between px-4 py-5 md:px-6 md:py-7">
        <BrandChip />
        {closeButton}
      </div>

      <nav className="flex flex-col gap-1 px-3">
        {NAV_ITEMS.map(({ path, icon, label }) => (
          <NavLink
            key={path}
            to={path}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm whitespace-nowrap no-underline transition-colors ${
                isActive
                  ? "bg-brand/10 text-brand-strong font-medium"
                  : "text-zinc-600 hover:bg-elevated hover:text-zinc-900"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <i
                  className={`bi ${icon} text-base ${
                    isActive ? "text-brand-strong" : "text-zinc-400"
                  }`}
                />
                {label}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* mt-auto pins this to the bottom whatever the nav length. */}
      <div className="border-hairline/60 mt-auto border-t px-3 py-4">
        <p
          className="truncate px-3 text-sm font-medium text-zinc-900"
          title={user?.fullname || user?.username}
        >
          {user?.fullname || user?.username}
        </p>
        <p className="px-3 text-xs text-zinc-500">Administrator</p>
        <Button
          variant="outline"
          onClick={() => authService.logout()}
          className="mt-3 w-full justify-start gap-3"
        >
          <i className="bi bi-box-arrow-right text-base text-zinc-400" />
          Sign out
        </Button>
      </div>
    </>
  );
};

/**
 * App shell: a static sidebar column on desktop, an off-canvas drawer on
 * mobile. The drawer is a Radix Dialog rather than a hand-rolled panel so that
 * focus trapping, Escape and — importantly — body scroll locking have a single
 * owner. A manual `body.style.overflow` lock here used to fight Radix's own
 * lock: closing the drawer while a modal was open unlocked the page behind it.
 */
const SidebarComponent: React.FC<SidebarComponentProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // Close the drawer whenever the route changes (e.g. after tapping a link).
  useEffect(() => setOpen(false), [pathname]);

  // Resizing to desktop while the drawer is open would leave an invisible
  // (md:hidden) dialog still trapping focus and holding the scroll lock.
  useEffect(() => {
    if (isDesktop) setOpen(false);
  }, [isDesktop]);

  return (
    <div className="bg-canvas flex min-h-dvh text-zinc-700 md:flex-row">
      {/* Mobile top bar: just the hamburger and brand. Hidden on desktop. */}
      <header className="border-hairline/60 bg-surface/80 fixed inset-x-0 top-0 z-30 flex h-14 items-center gap-2 border-b px-3 backdrop-blur md:hidden">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          aria-expanded={open}
          className="hover:bg-elevated rounded-lg p-2 text-zinc-500 transition-colors hover:text-zinc-900"
        >
          <i className="bi bi-list text-lg" />
        </button>
        <BrandChip className="py-1.5" />
      </header>

      {/* Static column, desktop only. */}
      <aside className="border-hairline/60 bg-surface hidden w-60 shrink-0 flex-col border-r md:flex">
        <SidebarNav />
      </aside>

      {/* Off-canvas drawer, mobile only. */}
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="animate-overlay-in fixed inset-0 z-40 bg-zinc-900/40 backdrop-blur-sm md:hidden" />
          <Dialog.Content
            aria-describedby={undefined}
            className="animate-drawer-in bg-surface border-hairline/70 fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r shadow-2xl shadow-zinc-900/10 md:hidden"
          >
            <Dialog.Title className="sr-only">Navigation</Dialog.Title>
            <SidebarNav
              closeButton={
                <Dialog.Close
                  aria-label="Close menu"
                  className="hover:bg-elevated rounded-lg p-2 text-zinc-500 transition-colors hover:text-zinc-900"
                >
                  <i className="bi bi-x-lg text-sm" />
                </Dialog.Close>
              }
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* pt-20 clears the fixed mobile bar; the bar is gone on desktop. */}
      <main className="min-w-0 flex-1 px-5 pt-20 pb-8 md:px-10 md:py-10">
        {/* Wider than the reference's max-w-5xl: admin's tables need the room. */}
        <div className="mx-auto max-w-6xl">{children}</div>
      </main>
    </div>
  );
};

export default SidebarComponent;
